import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { Send, Image, X, User, Trash2, Pencil, ChevronLeft, ChevronRight, FileText, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UnitLinkSelect, selectedJobUnit } from './UnitLinkSelect';
import { NoteUnitFilter } from './NoteUnitFilter';
import { extractErrorMessage } from '../../utils/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Component to load authenticated images with fallback handling
function AuthenticatedImage({ src, alt, className, onClick }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadImage = async () => {
      try {
        let fullUrl = src.startsWith('http') ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
        
        // Normalize paths - ensure /api/r2/ prefix for R2 images
        if (src.startsWith('/uploads/')) {
          const r2Path = '/api/r2' + src;
          fullUrl = `${process.env.REACT_APP_BACKEND_URL}${r2Path}`;
        }
        
        // For R2 images, fetch as blob via authenticated proxy
        if (fullUrl.includes('/api/r2/') || fullUrl.includes('/api/uploads/')) {
          const response = await axios.get(fullUrl, { responseType: 'blob' });
          if (isMounted) {
            const blobUrl = URL.createObjectURL(response.data);
            setImageSrc(blobUrl);
            setLoading(false);
          }
        } else {
          if (isMounted) {
            setImageSrc(fullUrl);
            setLoading(false);
          }
        }
      } catch (e) {
        console.warn(`Failed to load image: ${src}`, e.message);
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };
    loadImage();
    return () => { isMounted = false; };
  }, [src]);

  if (loading) return <div className={`${className} bg-slate-200 animate-pulse flex items-center justify-center`}><Image size={16} className="text-slate-400" /></div>;
  if (error) return <div className={`${className} bg-slate-200 flex items-center justify-center text-slate-400`} title="Image not available"><X size={16} /></div>;
  return <img src={imageSrc} alt={alt} className={className} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }} />;
}

export default function NotesSection({ jobId, notes, onReload, equipment = [] }) {
  const [newNote, setNewNote] = useState('');
  const [unitLink, setUnitLink] = useState('');
  const [unitFilter, setUnitFilter] = useState('all');
  const [savingNote, setSavingNote] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewImage, setViewImage] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [viewIndex, setViewIndex] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, note: null });
  const [editNote, setEditNote] = useState({ open: false, note: null, content: '' });
  const [dragOver, setDragOver] = useState(false);
  // PDF preview state — per attachment-path: { blobUrl, loading, error, open }
  const [pdfPreviews, setPdfPreviews] = useState({});
  // Track ALL blob URLs ever created so we can revoke them on unmount.
  const blobUrlsRef = useRef(new Set());
  const fileRef = useRef(null);
  const { user } = useAuth();
  const selectedUnit = selectedJobUnit(equipment, unitLink);
  const editedUnit = selectedJobUnit(equipment, editNote.equipment_item_id ?? editNote.note?.equipment_item_id ?? '');
  const needsUnit = equipment.length > 1 && !selectedUnit;
  const editNeedsUnit = equipment.length > 1 && !editedUnit;
  const currentFilter = equipment.length > 1 && (unitFilter === 'general' || equipment.some((e) => e.id === unitFilter)) ? unitFilter : 'all';
  const visibleNotes = notes.filter((n) => currentFilter === 'all' || (currentFilter === 'general' ? !equipment.some((e) => e.id === n.equipment_item_id) : n.equipment_item_id === currentFilter));

  // Revoke any cached blob URLs on unmount.
  useEffect(() => {
    const urls = blobUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const canModifyNote = (note) => {
    return user?.role === 'management' || note.user_id === user?.id;
  };

  const openGallery = (noteAttachments, clickedIdx) => {
    const images = (noteAttachments || []).filter(a => a.startsWith('/api/') || a.startsWith('http'));
    setAllImages(images);
    setViewIndex(clickedIdx);
    setViewImage(images[clickedIdx]);
  };

  const navigateGallery = (dir) => {
    const newIdx = viewIndex + dir;
    if (newIdx >= 0 && newIdx < allImages.length) {
      setViewIndex(newIdx);
      setViewImage(allImages[newIdx]);
    }
  };


  const handleDeleteNote = async () => {
    if (!deleteConfirm.note) return;
    try {
      await axios.delete(`${API}/notes/${deleteConfirm.note.id}`);
      toast.success('Note deleted');
      setDeleteConfirm({ open: false, note: null });
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete note');
    }
  };

  const handleEditNote = async () => {
    if (savingNote || !editNote.note || (!editNote.content.trim() && !editNote.note.attachments?.length)) return;
    if (editNeedsUnit) return toast.error('Select asset before saving this note');
    setSavingNote(true);
    try {
      const patch = { content: editNote.content };
      if (equipment.length || (editNote.equipment_item_id !== undefined && editNote.equipment_item_id !== (editNote.note.equipment_item_id || ''))) patch.equipment_item_id = editedUnit;
      await axios.patch(`${API}/notes/${editNote.note.id}`, patch);
      toast.success('Note updated');
      setEditNote({ open: false, note: null, content: '' });
      onReload();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update note'));
    } finally {
      setSavingNote(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploading) return;
    if (needsUnit) return toast.error('Select asset before adding this note');
    if (!newNote.trim() && !images.length) return;
    setUploading(true);
    try {
      // Upload images first if any
      const uploadedUrls = [];
      const attachmentNames = {};
      for (const img of images) {
        const formData = new FormData();
        formData.append('file', img.file);
        const res = await axios.post(`${API}/jobs/${jobId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedUrls.push(res.data.url);
        attachmentNames[res.data.url] = img.file.name;
      }

      await axios.post(`${API}/notes`, {
        job_id: jobId,
        content: newNote,
        attachments: uploadedUrls,
        attachment_names: attachmentNames,
        equipment_item_id: selectedUnit,
      });
      setNewNote('');
      setUnitLink('');
      setUnitFilter('all');
      images.forEach(i => URL.revokeObjectURL(i.preview));
      setImages([]);
      onReload();
      toast.success('Note added');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to add note'));
    } finally {
      setUploading(false);
    }
  };

  const addFiles = (fileList) => {
    const all = Array.from(fileList);
    const files = all.filter(f => f.type.startsWith('image/') || f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (files.length < all.length) toast.error('Only images and PDFs are supported');
    setImages(prev => [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))]);
  };

  const handleFiles = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const removeImg = (i) => {
    setImages(prev => { const arr = [...prev]; URL.revokeObjectURL(arr[i].preview); arr.splice(i, 1); return arr; });
  };

  // Resolve a stored attachment path to a fully-qualified URL routed through
  // the authenticated R2 proxy when needed.
  const attachmentUrl = (src) => {
    let fullUrl = src.startsWith('http') ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
    if (src.startsWith('/uploads/')) {
      const r2Path = '/api/r2' + src;
      fullUrl = `${process.env.REACT_APP_BACKEND_URL}${r2Path}`;
    }
    return fullUrl;
  };

  // Fetch a PDF as a blob URL once and cache it under `src`.
  const loadPdfBlob = async (src) => {
    if (pdfPreviews[src]?.blobUrl) return pdfPreviews[src].blobUrl;
    setPdfPreviews((prev) => ({ ...prev, [src]: { ...prev[src], loading: true, error: null, open: true } }));
    try {
      const response = await axios.get(attachmentUrl(src), { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      blobUrlsRef.current.add(blobUrl);
      setPdfPreviews((prev) => ({ ...prev, [src]: { blobUrl, loading: false, error: null, open: true } }));
      return blobUrl;
    } catch (err) {
      let detail;
      try {
        if (err.response?.data instanceof Blob) {
          detail = JSON.parse(await err.response.data.text())?.detail;
        } else {
          detail = err.response?.data?.detail;
        }
      } catch { /* leave detail undefined */ }
      const message = detail || 'Failed to load PDF';
      setPdfPreviews((prev) => ({ ...prev, [src]: { blobUrl: null, loading: false, error: message, open: true } }));
      toast.error(message);
      return null;
    }
  };

  // Click the chip: toggle the inline preview (lazy-loads on first open).
  const togglePdfPreview = async (src) => {
    const existing = pdfPreviews[src];
    if (existing?.open) {
      setPdfPreviews((prev) => ({ ...prev, [src]: { ...prev[src], open: false } }));
      return;
    }
    if (existing?.blobUrl) {
      setPdfPreviews((prev) => ({ ...prev, [src]: { ...prev[src], open: true } }));
      return;
    }
    await loadPdfBlob(src);
  };

  // Open the PDF in a new tab — used by the "Open in new tab" button inside
  // the inline preview, and as the iOS-friendly escape hatch.
  const openPdfInNewTab = async (src) => {
    const win = window.open('about:blank', '_blank');
    const blobUrl = pdfPreviews[src]?.blobUrl || await loadPdfBlob(src);
    if (!blobUrl) {
      if (win) win.close();
      return;
    }
    if (win) {
      win.location.href = blobUrl;
    } else {
      window.location.href = blobUrl;
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="notes-section">
      <h4 className="text-lg font-bold text-slate-900 mb-4">Notes & Attachments</h4>
      <form
        onSubmit={handleSubmit}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
        className={`mb-6 space-y-3 rounded-md transition-colors ${dragOver ? 'outline-dashed outline-2 outline-slate-400 bg-slate-50' : ''}`}
        data-testid="note-form"
      >
        <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add note... (drop images or PDFs anywhere here)" className="min-h-[80px]" data-testid="note-input" />
        <UnitLinkSelect equipment={equipment} value={selectedUnit} onChange={setUnitLink} testid="note-unit-select" disabled={uploading} jobNotes />
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {images.map((img) => (
              <div key={img.preview} className="relative">
                {img.file.type === 'application/pdf' ? (
                  <div className="w-16 h-16 rounded border bg-red-50 flex flex-col items-center justify-center">
                    <FileText size={20} className="text-red-500" />
                    <span className="text-[8px] text-red-500 font-bold mt-0.5">PDF</span>
                  </div>
                ) : (
                  <img src={img.preview} alt="" className="w-16 h-16 object-cover rounded border" />
                )}
                <button type="button" disabled={uploading} onClick={() => removeImg(images.indexOf(img))} aria-label="Remove attachment" data-testid={`remove-note-attachment-${images.indexOf(img)}`} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"><X size={10} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input type="file" ref={fileRef} onChange={handleFiles} accept="image/*,.pdf" multiple className="hidden" data-testid="file-input" />
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} size="sm" data-testid="attach-btn"><Image size={16} className="mr-1" />Attach</Button>
          <Button type="submit" disabled={uploading || needsUnit || (!newNote.trim() && !images.length)} size="sm" className="bg-slate-900" data-testid="submit-note-btn"><Send size={16} className="mr-1" />{uploading ? 'Uploading...' : 'Add'}</Button>
        </div>
      </form>
      {equipment.length > 1 && <NoteUnitFilter equipment={equipment} value={currentFilter} onChange={setUnitFilter} />}
      {!visibleNotes.length ? <p className="text-slate-500 text-center py-4" data-testid="no-notes">{notes.length ? 'No notes for this asset' : 'No notes yet'}</p> : (
        <div className="space-y-3" data-testid="notes-list">
          {visibleNotes.map(n => (
            <div key={n.id} className="p-3 bg-slate-50 rounded-lg relative group" data-testid={`note-${n.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <User size={12} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-700">{n.user_name}</span>
                <span className="text-xs text-slate-400">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                {canModifyNote(n) && (
                  <div className="ml-auto flex gap-1 sm:opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditNote({ open: true, note: n, content: n.content || '', equipment_item_id: n.equipment_item_id || '' })}
                      className="p-1 hover:bg-slate-200 rounded text-slate-500"
                      title="Edit note"
                      data-testid={`edit-note-${n.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm({ open: true, note: n })}
                      className="p-1 hover:bg-red-100 rounded text-red-500"
                      title="Delete note"
                      data-testid={`delete-note-${n.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              {n.content && <p className="text-slate-800 text-sm whitespace-pre-wrap [overflow-wrap:anywhere]" data-testid={`note-content-${n.id}`}>{n.content}</p>}
              {n.unit_ref && <p className="text-xs text-sky-700 mt-1 [overflow-wrap:anywhere]" data-testid={`note-unit-link-${n.id}`}>{[(n.unit_ref.label || `Asset ${n.unit_ref.unit_id || n.unit_ref.serial_number || ''}`).replace(/^(Unit|Equipment) /, 'Asset '), n.unit_ref.site?.lsd || n.unit_ref.site?.name].filter(Boolean).join(' · ')}</p>}
              {n.attachments?.length > 0 && (() => {
                const pdfAttachments = n.attachments.filter((a) => a.toLowerCase().endsWith('.pdf'));
                const imageAttachments = n.attachments.filter((a) => !a.toLowerCase().endsWith('.pdf'));
                return (
                  <div className="mt-2 flex flex-col gap-2">
                    {imageAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {imageAttachments.map((a, idx) => (
                          (a.startsWith('/api/') || a.startsWith('http')) ? (
                            <div
                              key={`${n.id}-img-${idx}`}
                              className="cursor-pointer"
                              onClick={() => openGallery(imageAttachments, idx)}
                            >
                              <AuthenticatedImage
                                src={a}
                                alt="attachment"
                                className="w-24 h-24 object-cover rounded border hover:opacity-80 transition-opacity"
                              />
                            </div>
                          ) : (
                            <span key={`${n.id}-img-${idx}`} className="text-xs bg-slate-200 px-2 py-0.5 rounded flex items-center gap-1">
                              <Image size={10} />{a}
                            </span>
                          )
                        ))}
                      </div>
                    )}
                    {pdfAttachments.map((a, idx) => {
                      const state = pdfPreviews[a] || {};
                      const isOpen = !!state.open;
                      return (
                        <div key={`${n.id}-pdf-${idx}`} className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => togglePdfPreview(a)}
                              className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                              data-testid={`pdf-attachment-${n.id}-${idx}`}
                              aria-expanded={isOpen}
                            >
                              <FileText size={16} className="text-red-500" />
                              <span className="text-xs font-medium text-red-700">PDF Document</span>
                              {isOpen ? (
                                <ChevronUp size={14} className="text-red-500" />
                              ) : (
                                <ChevronDown size={14} className="text-red-500" />
                              )}
                            </button>
                            {isOpen && state.blobUrl && (
                              <button
                                type="button"
                                onClick={() => openPdfInNewTab(a)}
                                className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100"
                                title="Open in new tab"
                                data-testid={`pdf-open-new-tab-${n.id}-${idx}`}
                              >
                                <ExternalLink size={12} />Open in new tab
                              </button>
                            )}
                          </div>
                          {isOpen && (
                            <div className="mt-2 w-full max-w-3xl rounded border border-slate-200 bg-white overflow-hidden" data-testid={`pdf-preview-${n.id}-${idx}`}>
                              {state.loading && (
                                <div className="flex items-center justify-center h-32 text-sm text-slate-500" data-testid={`pdf-preview-loading-${n.id}-${idx}`}>
                                  Loading PDF…
                                </div>
                              )}
                              {state.error && !state.loading && (
                                <div className="flex flex-col items-center justify-center h-32 gap-2 text-sm text-red-600" data-testid={`pdf-preview-error-${n.id}-${idx}`}>
                                  <span>{state.error}</span>
                                  <button
                                    type="button"
                                    onClick={() => loadPdfBlob(a)}
                                    className="text-xs px-2 py-1 border border-red-300 rounded hover:bg-red-50"
                                  >
                                    Retry
                                  </button>
                                </div>
                              )}
                              {!state.loading && !state.error && state.blobUrl && (
                                <object
                                  data={state.blobUrl}
                                  type="application/pdf"
                                  className="w-full block"
                                  style={{ height: '600px' }}
                                  aria-label="PDF preview"
                                >
                                  <div className="p-6 text-sm text-slate-600 text-center">
                                    Your browser can&apos;t display this PDF inline.{' '}
                                    <button
                                      type="button"
                                      onClick={() => openPdfInNewTab(a)}
                                      className="text-blue-600 underline"
                                    >
                                      Open in a new tab
                                    </button>{' '}
                                    instead.
                                  </div>
                                </object>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Delete Note Confirmation */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm({ open, note: open ? deleteConfirm.note : null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This will also remove any attached images. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteNote} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-note">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Note Dialog */}
      <Dialog open={editNote.open} onOpenChange={(open) => { if (!savingNote) setEditNote((prev) => open ? { ...prev, open } : { open: false, note: null, content: '' }); }}>
        <DialogContent className="w-[calc(100%-1rem)] grid-cols-[minmax(0,1fr)]" data-testid="edit-note-dialog">
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription className="sr-only">Note content and linked asset</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={editNote.content}
              onChange={(e) => setEditNote({ ...editNote, content: e.target.value })}
              placeholder="Edit note content..."
              className="min-h-[100px]"
              data-testid="edit-note-input"
            />
            <UnitLinkSelect equipment={equipment} value={editedUnit} onChange={(equipment_item_id) => setEditNote({ ...editNote, equipment_item_id })} testid="edit-note-unit-select" disabled={savingNote} jobNotes />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" disabled={savingNote} onClick={() => setEditNote({ open: false, note: null, content: '' })} data-testid="cancel-edit-note-btn">Cancel</Button>
              <Button onClick={handleEditNote} disabled={savingNote || editNeedsUnit || (!editNote.content.trim() && !editNote.note?.attachments?.length)} className="bg-slate-900" data-testid="save-edit-note-btn">{savingNote ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Gallery Modal */}
      <Dialog open={!!viewImage} onOpenChange={() => { setViewImage(null); setAllImages([]); }}>
        <DialogContent className="max-w-4xl p-2"
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') navigateGallery(-1);
            if (e.key === 'ArrowRight') navigateGallery(1);
          }}
        >
          {viewImage && (
            <div className="relative" data-testid="image-gallery">
              <AuthenticatedImage 
                src={viewImage} 
                alt="Full size" 
                className="w-full h-auto max-h-[80vh] object-contain rounded" 
              />
              {allImages.length > 1 && (
                <>
                  {viewIndex > 0 && (
                    <button
                      onClick={() => navigateGallery(-1)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
                      data-testid="gallery-prev"
                    >
                      <ChevronLeft size={24} />
                    </button>
                  )}
                  {viewIndex < allImages.length - 1 && (
                    <button
                      onClick={() => navigateGallery(1)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
                      data-testid="gallery-next"
                    >
                      <ChevronRight size={24} />
                    </button>
                  )}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full">
                    {viewIndex + 1} / {allImages.length}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
